<template>
  <FlexCol>
    <h5 class="card-title mb-2 fw-bold fs-3">Let's start with some basics</h5>

    <p>Choose your location and business type to get started.</p>
    <div class="row mt-3">
      <div class="col">
        <b> Brand Name </b>
        <div class="row mt-1">
          <div class="col me-1">
            <input
              v-model="store.brandName"
              class="form-control"
              :class="store.brandName ? 'is-valid' : 'is-invalid'"
              type="text"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="row mt-3">
      <div class="col">
        <b> Industry </b>
        <div class="row mt-1 g-2">
          <div
            class="col-6 col-md-3"
            v-for="industry in industries"
            :key="industry.id"
          >
            <div
              class="card h-100 industry-card"
              :class="{ selected: industry.id === store.brandIndustry.id }"
              @click="
                store.brandIndustry = industry;
                store.brandEarningSelection = industry.mode;
              "
            >
              <div
                class="card-body p-2 d-flex flex-column align-items-center justify-content-center"
              >
                <img
                  v-if="industry.icon"
                  :src="industry.icon"
                  class="industry-icon mb-1"
                />
                <span class="text-center">{{ industry.name }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <TimezoneSelector
      :title="'Timezone & Location'"
      class="mt-3"
      :show-country="true"
      :country="store.brandCountry"
      @update="
        (data) => {
          store.brandTimeZone = data.timezone;
          store.brandCountry = data.country;
          selectTimezoneStore(data.timezone);
        }
      "
    />
    <div>
      <div class="row mt-3">
        <div class="col me-1">
          <b>Store</b>
        </div>
      </div>
      <div class="row">
        <div class="col"></div>
      </div>
      <div class="row mt-3 gx-0">
        <div class="col">
          <input
            v-model="location"
            class="form-control"
            :class="location ? 'is-valid' : 'is-invalid'"
            type="text"
            placeholder="Location of store"
          />
        </div>
      </div>
      <div class="row mt-3 gx-0">
        <div class="col">
          <input
            class="form-control custom-font"
            type="text"
            :value="store.brandName + ', ' + location"
            style="font-style: italic"
            disabled
          />
        </div>
      </div>
      <TimezoneSelector
        :show-country="false"
        title="Timezone"
        class="mt-3"
        :country="store.brandCountry"
        @update="
          (data) => {
            selectTimezoneStore(data.timezone);
          }
        "
      />
      <div class="row mt-3">
        <div class="col">
          <button
            class="btn btn-action"
            style="font-size: 12px"
            :disabled="!isValidLocation"
            @click="addOutlet"
          >
            + Add Location
          </button>

          <div
            v-for="(outlet, index) in store.outlets"
            :key="index"
            class="outlet card mt-3"
            style="background-color: whitesmoke"
          >
            <div class="row">
              <div class="col ms-3 mt-3 mb-3">
                <p style="font-size: 14px">
                  Store #{{ index + 1 }} - {{ store.brandName }},
                  {{ outlet.outletLocation }},
                  {{ outlet.label }}
                </p>
              </div>
              <div class="col-2 text-end">
                <button class="btn btn-link" @click="deleteOutlet(index)">
                  <i
                    class="bi bi-x-lg"
                    style="color: #b7b7b7; margin-left: -0.5rem"
                  ></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </FlexCol>
  <PopupFreeStore :specification="'add_store'" />
</template>

<script setup>
import { useBrandSetupStore } from "@/stores/brand";
import { computed, ref, watch } from "vue";
import PopupFreeStore from "@/pages/AccountManagement/Store/AddStore/PopupFreeStore.vue";
import FandBIcon from "@/pages/AccountManagement/UserOnboarding/Icons/ICO62.svg";
import RetailIcon from "@/pages/AccountManagement/UserOnboarding/Icons/ICO133.svg";
import ServicesIcon from "@/pages/AccountManagement/UserOnboarding/Icons/ICO25.svg";
import TimezoneSelector from "./Components/TimezoneSelector.vue";
import FlexCol from "@/layout/FlexCol.vue";
import { useQuery } from "@urql/vue";
import { INDUSTRIES } from "@/constants/graphql";

const emit = defineEmits(["update-brand"]);

const store = useBrandSetupStore();
const selectedTimezoneStore = ref("");

const location = ref("");
const industries = ref([]);

const isValidLocation = computed(() => {
  return location.value.trim() !== "" && selectedTimezoneStore.value !== "";
});

const selectTimezoneStore = (time) => {
  selectedTimezoneStore.value = time;
};
const addOutlet = () => {
  store.outlets.push({
    outletLocation: location.value,
    outletTimezone: selectedTimezoneStore.value.zone,
    label: selectedTimezoneStore.value.label,
  });
};

const deleteOutlet = (index) => {
  store.outlets.splice(index, 1);
};

const industryQuery = useQuery({
  query: INDUSTRIES,
});

watch(industryQuery.fetching, (fetching) => {
  if (!fetching && industryQuery.data.value.industry) {
    const getIcon = (name) => {
      switch (name) {
        case "F&B":
          return FandBIcon;
        case "Retail":
          return RetailIcon;
        case "Service":
          return ServicesIcon;
        default:
          return null;
      }
    };

    const data = industryQuery.data.value.industry;
    industries.value = data.map((item) => ({
      ...item,
      name: item.name === "F&B" ? "Restaurant" : item.name,
      icon: getIcon(item.name),
      mode: item.mode === "earn_on_spend" ? "dynamic" : "fixed",
    }));
  }
});
</script>

<style scoped>
.industry-card {
  cursor: pointer;
  border: 1px solid #ced4da;
  border-radius: 1rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.industry-card:hover {
  background-color: #f8f9fa;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.industry-card.selected {
  border-color: black;
  border-width: 2px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.industry-icon {
  width: 40px;
  height: 40px;
}
</style>
